// src/pages/GiftMoment.tsx
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaBirthdayCake,
  FaBriefcase,
  FaCalendarAlt,
  FaCheck,
  FaExternalLinkAlt,
  FaGift,
  FaHeart,
  FaMapMarkedAlt,
  FaQuestionCircle,
  FaShieldAlt,
  FaSmileBeam,
  FaMagic,
  FaStore,
  FaUsers,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import ExternalForfeoRedirectModal from '../components/ExternalForfeoRedirectModal';
import { FORFEO_GIFT_CARD_URL } from '../lib/forfeoExternalLinks';

type GiftCard = {
  icon: React.ReactNode;
  title: string;
  text: string;
  link?: string;
};

export default function GiftMoment() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');
  const [isRedirectOpen, setIsRedirectOpen] = useState(false);

  const copy = useMemo(() => ({
    seoTitle: isEn ? 'Offer a local moment | Forfeo Explore' : 'Offrir un moment local | Forfeo Explore',
    seoDescription: isEn
      ? 'Find a meaningful local gift idea, then buy the official Forfeo virtual card securely on forfeo.com.'
      : 'Trouvez une idée cadeau locale qui a du sens, puis achetez la carte virtuelle Forfeo officielle en toute sécurité sur forfeo.com.',
    eyebrow: isEn ? 'Gift a real moment' : 'Offrir un vrai moment',
    title: isEn ? 'Give more than a gift. Give a memory.' : 'Offrez plus qu’un cadeau. Offrez un souvenir.',
    subtitle: isEn
      ? 'Forfeo Explore helps you choose the right local moment: a getaway, a dinner, a relaxing experience, a family outing or a flexible Forfeo virtual card.'
      : 'Forfeo Explore vous aide à choisir le bon moment local : une escapade, un souper, une expérience détente, une sortie en famille ou une carte virtuelle Forfeo flexible.',
    primaryCta: isEn ? 'Buy the Forfeo card' : 'Acheter la carte Forfeo',
    secondaryCta: isEn ? 'Explore gift ideas' : 'Explorer les idées cadeaux',
    leaveNote: isEn
      ? 'The Forfeo virtual card is purchased on forfeo.com, Forfeo’s official transactional storefront.'
      : 'La carte virtuelle Forfeo s’achète sur forfeo.com, la vitrine transactionnelle officielle de Forfeo.',
    whyTitle: isEn ? 'Start with the emotion, not the product.' : 'Commencez par l’émotion, pas par le produit.',
    whyText: isEn
      ? 'Some people want calm, others want adventure, discovery, good food or a surprise. This page guides the choice, while Forfeo.com handles the secure card purchase.'
      : 'Certaines personnes veulent du calme, d’autres de l’aventure, de la découverte, de la gastronomie ou une surprise. Cette page guide le choix, pendant que Forfeo.com gère l’achat sécurisé de la carte.',
    occasionsTitle: isEn ? 'Choose the occasion' : 'Choisissez l’occasion',
    stylesTitle: isEn ? 'Choose the kind of moment' : 'Choisissez le style de moment',
    howTitle: isEn ? 'How it works' : 'Comment ça fonctionne',
    bridgeTitle: isEn ? 'A clear bridge between both Forfeo sites' : 'Une passerelle claire entre les deux sites Forfeo',
    bridgeText: isEn
      ? 'Forfeo Explore is for discovery and inspiration. Forfeo.com is the official purchase tunnel for the virtual Forfeo card. We warn the user before leaving Explore so the experience stays clear and trustworthy.'
      : 'Forfeo Explore sert à découvrir et s’inspirer. Forfeo.com est le tunnel d’achat officiel de la carte virtuelle Forfeo. L’utilisateur est averti avant de quitter Explore pour garder une expérience claire et rassurante.',
    finalTitle: isEn ? 'Ready to offer something that feels local?' : 'Prêt à offrir quelque chose qui se vit vraiment ?',
    finalText: isEn
      ? 'Choose a flexible card on forfeo.com or keep exploring local experiences on Forfeo Explore.'
      : 'Choisissez une carte flexible sur forfeo.com ou continuez à explorer les expériences locales sur Forfeo Explore.',
    stayExplore: isEn ? 'Stay on Explore' : 'Rester sur Explore',
  }), [isEn]);

  const occasions: GiftCard[] = [
    { icon: <FaBirthdayCake />, title: isEn ? 'Birthday' : 'Anniversaire', text: isEn ? 'A flexible gift when you do not know exactly what they want.' : 'Un cadeau flexible quand on ne sait pas exactement quoi choisir.' },
    { icon: <FaHeart />, title: isEn ? 'Couple moment' : 'Moment à deux', text: isEn ? 'A dinner, spa, stay or quiet escape to share.' : 'Un souper, un spa, un séjour ou une escapade tranquille à partager.' },
    { icon: <FaUsers />, title: isEn ? 'Family' : 'Famille', text: isEn ? 'Ideas for outings, discoveries and simple memories.' : 'Des idées de sorties, découvertes et souvenirs simples.' },
    { icon: <FaBriefcase />, title: isEn ? 'Employee or client gift' : 'Employé ou client', text: isEn ? 'A premium local gift that feels more personal than a generic card.' : 'Un cadeau local premium, plus humain qu’une carte générique.' },
  ];

  const styles: GiftCard[] = [
    { icon: <FaMagic />, title: isEn ? 'Surprise reveal' : 'Révélation surprise', text: isEn ? 'Create a small wow effect before the recipient discovers the moment.' : 'Créez un effet wow avant que la personne découvre le moment.', link: '/revelation-surprise' },
    { icon: <FaMapMarkedAlt />, title: isEn ? 'Discover Quebec' : 'Découvrir le Québec', text: isEn ? 'Perfect for visitors planning a stay before arrival.' : 'Parfait pour un visiteur qui prépare son arrivée.', link: '/decouvrir-quebec' },
    { icon: <FaStore />, title: isEn ? 'Local commerce' : 'Commerce local', text: isEn ? 'Support businesses in the Forfeo ecosystem.' : 'Soutenir les entreprises de l’écosystème Forfeo.' },
    { icon: <FaSmileBeam />, title: isEn ? 'Last-minute gift' : 'Dernière minute', text: isEn ? 'A digital gift that can be sent quickly.' : 'Un cadeau numérique qui peut être envoyé rapidement.' },
  ];

  const steps = [
    isEn ? 'Get inspired on Forfeo Explore.' : 'Inspirez-vous sur Forfeo Explore.',
    isEn ? 'Choose whether a flexible card or a specific experience is best.' : 'Choisissez si une carte flexible ou une expérience précise convient mieux.',
    isEn ? 'When ready to buy the Forfeo virtual card, continue securely to forfeo.com.' : 'Au moment d’acheter la carte virtuelle Forfeo, continuez vers forfeo.com en toute sécurité.',
    isEn ? 'Come back to Explore to discover ideas, regions and offers.' : 'Revenez sur Explore pour découvrir des idées, régions et offres.',
  ];

  const openForfeoCard = () => setIsRedirectOpen(true);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 font-sans overflow-hidden">
      <Header />
      <SeoHead title={copy.seoTitle} description={copy.seoDescription} canonicalPath="/offrir-un-moment" />
      <ExternalForfeoRedirectModal
        isOpen={isRedirectOpen}
        onClose={() => setIsRedirectOpen(false)}
        targetUrl={FORFEO_GIFT_CARD_URL}
        contextLabel="forfeo.com/carte-cadeau"
      />

      <main className="pt-24">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-20 text-white md:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(59,130,246,0.38),transparent_34%),radial-gradient(circle_at_90%_10%,rgba(249,115,22,0.30),transparent_30%),linear-gradient(135deg,#020617,#0f172a_48%,#172554)]" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:34px_34px]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.03fr_0.97fr]">
            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100 backdrop-blur-md">
                <FaGift className="text-cyan-300" /> {copy.eyebrow}
              </div>
              <h1 className="max-w-4xl text-5xl font-black leading-[0.92] tracking-tighter md:text-7xl lg:text-8xl">{copy.title}</h1>
              <p className="mt-7 max-w-2xl text-base font-semibold leading-relaxed text-slate-300 md:text-xl">{copy.subtitle}</p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button onClick={openForfeoCard} className="group inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-950 shadow-2xl transition hover:-translate-y-0.5 hover:bg-cyan-50">
                  {copy.primaryCta} <FaExternalLinkAlt className="transition group-hover:translate-x-1" />
                </button>
                <button onClick={() => navigate('/boutique')} className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-8 py-4 text-xs font-black uppercase tracking-widest text-white backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-white/15">
                  {copy.secondaryCta} <FaArrowRight />
                </button>
              </div>
              <div className="mt-6 flex items-start gap-3 rounded-3xl border border-white/10 bg-white/5 p-4 text-sm font-semibold leading-relaxed text-slate-300 backdrop-blur-md">
                <FaShieldAlt className="mt-0.5 shrink-0 text-cyan-300" />
                <span>{copy.leaveNote}</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96, y: 18 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.1 }} className="relative">
              <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-cyan-400/20 via-blue-500/15 to-orange-400/20 blur-3xl" />
              <div className="relative rounded-[3rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur-2xl">
                <div className="overflow-hidden rounded-[2.4rem] border border-white/10 bg-slate-950">
                  <div className="h-80 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=1600&auto=format&fit=crop')" }} />
                  <div className="p-6">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-cyan-100">
                      <FaCalendarAlt /> {isEn ? 'Any occasion' : 'Toutes occasions'}
                    </div>
                    <h2 className="text-3xl font-black tracking-tight text-white">Forfeo</h2>
                    <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-400">{copy.bridgeText}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20">
          <div className="mb-10 max-w-3xl">
            <h2 className="text-4xl font-black tracking-tight md:text-5xl">{copy.whyTitle}</h2>
            <p className="mt-4 text-lg font-medium leading-relaxed text-slate-600">{copy.whyText}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {occasions.map((item) => (
              <div key={item.title} className="group rounded-[2rem] border border-slate-100 bg-white p-7 shadow-xl shadow-slate-200/50 transition hover:-translate-y-1 hover:shadow-2xl">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-2xl text-blue-600 transition group-hover:scale-110 group-hover:bg-slate-950 group-hover:text-white">{item.icon}</div>
                <h3 className="text-xl font-black text-slate-950">{item.title}</h3>
                <p className="mt-3 text-sm font-medium leading-relaxed text-slate-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white px-4 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">Forfeo Explore</p>
                <h2 className="mt-3 text-4xl font-black tracking-tight md:text-5xl">{copy.stylesTitle}</h2>
              </div>
              <button onClick={openForfeoCard} className="inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-7 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:bg-blue-700">
                {copy.primaryCta} <FaExternalLinkAlt />
              </button>
            </div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {styles.map((item) => (
                <button key={item.title} onClick={() => item.link ? navigate(item.link) : undefined} className="rounded-[2rem] border border-slate-100 bg-slate-50 p-6 text-left transition hover:-translate-y-1 hover:bg-white hover:shadow-xl">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl text-slate-950 shadow-sm">{item.icon}</div>
                  <h3 className="text-lg font-black text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-600">{item.text}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-20">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="rounded-[2.5rem] bg-slate-950 p-8 text-white shadow-2xl md:p-10">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-cyan-100">
                <FaQuestionCircle /> {copy.howTitle}
              </div>
              <h2 className="text-4xl font-black tracking-tight md:text-5xl">{copy.bridgeTitle}</h2>
              <p className="mt-5 text-base font-semibold leading-relaxed text-slate-300">{copy.bridgeText}</p>
            </div>
            <div className="grid gap-4">
              {steps.map((step, index) => (
                <div key={step} className="flex items-start gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">{index + 1}</div>
                  <p className="pt-2 text-sm font-bold leading-relaxed text-slate-700">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-24">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[3rem] bg-gradient-to-br from-blue-600 via-slate-950 to-cyan-600 p-8 text-center text-white shadow-2xl md:p-14">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-3xl bg-white/15 text-3xl backdrop-blur-md"><FaGift /></div>
            <h2 className="mx-auto max-w-3xl text-4xl font-black tracking-tight md:text-6xl">{copy.finalTitle}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base font-semibold leading-relaxed text-blue-100 md:text-lg">{copy.finalText}</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <button onClick={openForfeoCard} className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-8 py-4 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:-translate-y-0.5 hover:bg-blue-50">
                {copy.primaryCta} <FaExternalLinkAlt />
              </button>
              <Link to="/decouvrir-quebec" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-8 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:-translate-y-0.5 hover:bg-white/15">
                {copy.stayExplore} <FaArrowRight />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs font-black uppercase tracking-widest text-blue-100">
              <span className="inline-flex items-center gap-2"><FaCheck className="text-green-300" /> {isEn ? 'Secure bridge' : 'Passerelle sécurisée'}</span>
              <span className="inline-flex items-center gap-2"><FaCheck className="text-green-300" /> {isEn ? 'Official Forfeo purchase' : 'Achat officiel Forfeo'}</span>
              <span className="inline-flex items-center gap-2"><FaCheck className="text-green-300" /> {isEn ? 'Return to Explore anytime' : 'Retour Explore en tout temps'}</span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
